import {useCallback, useState} from 'react'

function reorderNodes<T extends {id: string} | null | undefined>(
  nodes: readonly T[] | null,
  dragId: string,
  dropId: string,
  isBefore: boolean,
): readonly T[] | null {
  if (!nodes || dragId === dropId) return nodes
  const from = nodes.findIndex(node => node?.id === dragId)
  if (from === -1 || nodes.findIndex(node => node?.id === dropId) === -1) return nodes

  const next = [...nodes]
  const moved = next.splice(from, 1)
  const dropIndex = next.findIndex(node => node?.id === dropId)
  next.splice(isBefore ? dropIndex : dropIndex + 1, 0, ...moved)
  return next
}

/**
 * Paints a reorder in the same commit as the drop, since the optimistic overlay applies a frame later.
 * Resets whenever `nodes` changes so confirmed and reverted server orders replace the local display order.
 */
export function useReorderDisplayOrder<T extends {id: string} | null | undefined>(
  nodes: readonly T[] | null,
  enabled: boolean,
) {
  const [order, setOrder] = useState<{synced: readonly T[] | null; display: readonly T[] | null}>({
    synced: nodes,
    display: nodes,
  })

  if (enabled && order.synced !== nodes) {
    setOrder({synced: nodes, display: nodes})
  }

  const applyLocalReorder = useCallback(
    (dragId: string, dropId: string, isBefore: boolean) => {
      if (!enabled) return
      setOrder(previous => ({
        ...previous,
        display: reorderNodes(previous.display, dragId, dropId, isBefore),
      }))
    },
    [enabled],
  )

  return {displayNodes: enabled ? order.display : nodes, applyLocalReorder}
}
